<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import {
  Printer,
  Download,
  Users,
  LogIn,
  LogOut,
  UserCheck,
  CalendarRange,
} from "@lucide/vue";
import { api, apiBlob } from "../api";
import { useSessionStore } from "../stores/session";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiDateInput from "../ui/UiDateInput.vue";
import UiToast from "../ui/UiToast.vue";
import UiStudentAvatar from "../ui/UiStudentAvatar.vue";
const window = globalThis.window;
const session = useSessionStore(),
  report = ref(null),
  preset = ref("today"),
  from = ref(""),
  to = ref(""),
  page = ref(1),
  loading = ref(false),
  toast = ref(""),
  toastType = ref("success"),
  perPage = 25;
const presets = [
  ["today", "Bugün"],
  ["yesterday", "Dün"],
  ["thisWeek", "Bu hafta"],
  ["lastWeek", "Geçen hafta"],
  ["thisMonth", "Bu ay"],
  ["lastMonth", "Geçen ay"],
  ["custom", "Özel tarih"],
];
const paged = computed(() => report.value?.logs || []);
function show(m, t = "error") {
  toast.value = m;
  toastType.value = t;
  setTimeout(() => (toast.value = ""), 3200);
}
function query() {
  const q = new URLSearchParams({
    preset: preset.value,
    limit: String(perPage),
    offset: String((page.value - 1) * perPage),
  });
  if (session.selectedSchoolId) q.set("schoolId", session.selectedSchoolId);
  if (preset.value === "custom") {
    q.set("from", from.value);
    q.set("to", to.value);
  }
  return q;
}
async function load() {
  loading.value = true;
  try {
    report.value = (await api(`/api/reports/access?${query()}`)).report;
  } catch (e) {
    show(e.message);
  } finally {
    loading.value = false;
  }
}
async function download() {
  try {
    const blob = await apiBlob(`/api/reports/access.pdf?${query()}`),
      url = URL.createObjectURL(blob),
      a = document.createElement("a");
    a.href = url;
    a.download = `gecis-raporu-${report.value.range.from}-${report.value.range.to}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    show(e.message);
  }
}
function choose(value) {
  preset.value = value;
  page.value = 1;
  if (value !== "custom") load();
}
const dt = (v) =>
  new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(v));
const refresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", refresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", refresh));
</script>
<template>
  <PageHeader
    title="Analiz ve raporlar"
    description="Erva Spor Okullarının katılım, hareket ve doluluk verilerini dönemsel karşılaştırın."
    ><button class="btn-secondary no-print" @click="window.print()">
      <Printer class="size-4" />Yazdır</button
    ><button class="btn-primary no-print" @click="download">
      <Download class="size-4" />PDF İndir
    </button></PageHeader
  >
  <section class="panel mb-5 p-4 no-print">
    <div class="flex flex-wrap items-end gap-2">
      <button
        v-for="item in presets"
        :key="item[0]"
        :class="[
          'h-9 rounded-md px-3 text-xs font-bold',
          preset === item[0]
            ? 'bg-ink-900 text-white'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
        ]"
        @click="choose(item[0])"
      >
        {{ item[1] }}</button
      ><template v-if="preset === 'custom'"
        ><label class="field ml-2"
          ><span class="field-label">Başlangıç</span
          ><UiDateInput v-model="from" class="w-44" /></label
        ><label class="field"
          ><span class="field-label">Bitiş</span
          ><UiDateInput v-model="to" class="w-44" /></label
        ><button class="btn-primary" @click="load">
          <CalendarRange class="size-4" />Uygula
        </button></template
      >
    </div>
  </section>
  <div v-if="loading" class="grid min-h-72 place-items-center">
    <div class="loader-ring"></div>
  </div>
  <template v-else-if="report"
    ><section class="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <article
        v-for="card in [
          { l: 'Toplam hareket', v: report.summary.totalLogs, i: Users },
          { l: 'Giriş', v: report.summary.entries, i: LogIn },
          { l: 'Çıkış', v: report.summary.exits, i: LogOut },
          { l: 'Şu an içeride', v: report.summary.insideNow, i: UserCheck },
          { l: 'Misafir geçişi', v: report.summary.guestLogs, i: Users },
        ]"
        :key="card.l"
        class="stat-card"
      >
        <div class="mb-3 flex items-center justify-between">
          <span class="text-xs font-bold text-slate-500">{{ card.l }}</span
          ><component :is="card.i" class="size-4 text-brand-600" />
        </div>
        <strong class="text-3xl font-extrabold text-ink-950">{{
          card.v
        }}</strong>
      </article>
    </section>
    <section class="panel mb-5 overflow-hidden">
      <div class="toolbar">
        <div>
          <h3 class="font-extrabold text-ink-950">Okul karşılaştırması</h3>
          <p class="text-xs text-slate-500">
            {{ report.range.from }} - {{ report.range.to }}
          </p>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>Okul</th>
              <th>Aktif öğrenci</th>
              <th>Giriş</th>
              <th>Çıkış</th>
              <th>Tekil kişi</th>
              <th>Misafir</th>
              <th>İçeride</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in report.schoolBreakdown" :key="r.school_id">
              <td class="font-bold text-ink-900">{{ r.school_name }}</td>
              <td>{{ r.activeStudents }}</td>
              <td>{{ r.entries }}</td>
              <td>{{ r.exits }}</td>
              <td>{{ r.uniquePeople }}</td>
              <td>{{ r.guestPeople }}</td>
              <td>
                <span class="chip-success">{{ r.insideNow }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="panel overflow-hidden">
      <div class="toolbar">
        <h3 class="font-extrabold text-ink-950">Rapor kayıtları</h3>
        <span class="text-xs font-semibold text-slate-500"
          >{{ report.logPage.total }} kayıt</span
        >
      </div>
      <div class="overflow-x-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>Zaman</th>
              <th>Öğrenci</th>
              <th>Okul</th>
              <th>Cihaz</th>
              <th>Yön</th>
              <th>Sonuç</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in paged" :key="r.id">
              <td>{{ dt(r.created_at) }}</td>
              <td>
                <div class="flex min-w-40 items-center gap-3">
                  <UiStudentAvatar
                    :src="r.photo_path"
                    :first-name="r.first_name"
                    :last-name="r.last_name"
                    size="sm"
                  />
                  <span class="font-semibold">{{
                    [r.first_name, r.last_name].filter(Boolean).join(" ") ||
                    "Bilinmeyen kart"
                  }}</span>
                </div>
              </td>
              <td>{{ r.school_name }}</td>
              <td>{{ r.device_name }}</td>
              <td>{{ r.direction === "entry" ? "Giriş" : "Çıkış" }}</td>
              <td>
                <span
                  :class="
                    r.decision === 'allowed' ? 'chip-success' : 'chip-danger'
                  "
                  >{{ r.decision === "allowed" ? "İzinli" : "Red" }}</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <TablePager
        v-model:page="page"
        :total="report.logPage.total"
        :per-page="perPage"
        @update:page="load"
      /></section></template
  ><UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
